// @ts-strict-ignore
import { Dictionary } from 'typescript-collections';
import { OBJRepresentationDataVO, RepresentationArticleVO, RepresentationDataVO, RepresentationVO } from '../vo/representation.vo';
import { LocalizedPropertyValueVO, PropertyValueVO, PropertyVO, ReportGenerationPropertyVO, StringValueInLocalesVO } from '@project/components/article/model/vo/property.vo';
import { CompressionSimpleTextValueVO, ConnectionAreaUsageVO, ConnectionAreaVO, MetaElementUsageVO, MetaElementVO, MontagePointAreaVO, SimpleTextValueVO, TableTextValueVO, TextureAreaUsageVO, TextureAreaVO, TextValueVO } from '../vo/meta-element.vo';
import { ArticleTechnicalInfoVO, ArticleVO } from '@article/model/vo/article.vo';
import { ArrayUtils } from '../util/array-utils';
import { JsonConvert } from './convert-core';
import { ProjectVO } from '@project/model/vo/project.vo';
import { TextSizeCheckRequestVO, TextSizeCheckVO } from '@3d-scene/manager/text-size-check.vo';
import { CustomArticleVO } from '@project/components/article/model/vo/custom-article.vo';
import { ApplyTextToTextureAreaUsageQuickFixActionVO, ArticleDescriptorVO, ArticleReferenceVO, ArticleUsageInfoPropertyReferenceVO, ArticleUsagePlausiPropertyReferenceVO, CalculatedArticlePropertyReferenceVO, DefinedMetaElementPlausiPropertyReferenceVO, DeleteArticleQuickFixActionVO, ElementReferenceVO, InsertArticleQuickFixActionVO, ListArticleDescriptorVO, QuickFixActionVO, SingleArticleDescriptorVO, UsageArticleDescriptorVO } from '@project/model/vo/validation-context.vo';

export class ConverterHelper {

  public static readonly TYPE_FIELD_NAME: string = 'type';

  private static hasObjectRequiredKeys(targetKeys: string[], objectKeys: string[]): boolean {
    if (targetKeys.length !== objectKeys.length) {
      return false;
    }
    for (const key of targetKeys) {
      if (!objectKeys.includes(key)) {
        return false;
      }
    }
    return true;
  }

  public static convertToRepresentationVOs(object: any): RepresentationVO[] {
    if (!object || !Array.isArray(object)) {
      return new Array<RepresentationVO>();
    }
    return JsonConvert.deserialize(object, '[RepresentationVO]');
  }
  public static convertToRepresentationArticleVOs(object: any): RepresentationArticleVO[] {
    if (!object || !Array.isArray(object)) {
      return new Array<RepresentationArticleVO>();
    }
    return JsonConvert.deserialize(object, '[RepresentationArticleVO]');
  }

  public static convertToTextSizeCheckRequestVO(object: any): TextSizeCheckRequestVO {
    if (!object || typeof object !== 'object') {
      return null;
    }
    return JsonConvert.deserialize(object, 'TextSizeCheckRequestVO');
  }

  public static convertToTextSizeCheckVOs(object: any): TextSizeCheckVO[] {
    if (!object || !Array.isArray(object)) {
      return new Array<TextSizeCheckVO>();
    }
    return JsonConvert.deserialize(object, '[TextSizeCheckVO]');
  }

  public static convertToStringValueInLocalesVO(object: any): StringValueInLocalesVO {
    if (!object || typeof object !== 'object') {
      return null;
    }
    return JsonConvert.deserialize(object, 'StringValueInLocalesVO');
  }

  public static convertToTextValueVO(object: any): TextValueVO {
    if (!object || typeof object !== 'object') {
      return null;
    }
    const type: string = object[this.TYPE_FIELD_NAME];
    if (type) {
      if (SimpleTextValueVO.CLASS_NAME === type) {
        return JsonConvert.deserialize(object, 'SimpleTextValueVO');
      }
      if (TableTextValueVO.CLASS_NAME === type) {
        return JsonConvert.deserialize(object, 'TableTextValueVO');
      }
      if (CompressionSimpleTextValueVO.CLASS_NAME === type) {
        return JsonConvert.deserialize(object, 'CompressionSimpleTextValueVO');
      }
    }
    return null;
  }

  public static convertToMetaElementUsageVOs(object: any): MetaElementUsageVO[] {
    if (!object || !Array.isArray(object)) {
      return new Array<MetaElementUsageVO>();
    }
    const targetObjects: MetaElementUsageVO[] = new Array<MetaElementUsageVO>();
    for (const entry of object) {
      const property: MetaElementUsageVO = ConverterHelper.convertToMetaElementUsageVO(entry);
      if (property) {
        targetObjects.push(property);
      }
    }
    return targetObjects;
  }

  public static convertToMetaElementUsageVO(object: any): MetaElementUsageVO {
    if (!object || typeof object !== 'object') {
      return null;
    }
    const type: string = object[this.TYPE_FIELD_NAME];
    if (type) {
      if (MetaElementUsageVO.CLASS_NAME === type) {
        return JsonConvert.deserialize(object, 'MetaElementUsageVO');
      }
      if (ConnectionAreaUsageVO.CLASS_NAME === type) {
        return JsonConvert.deserialize(object, 'ConnectionAreaUsageVO');
      }
      if (TextureAreaUsageVO.CLASS_NAME === type) {
        return JsonConvert.deserialize(object, 'TextureAreaUsageVO');
      }
    }
    return null;
  }

  public static convertToMetaElementVOs(object: any): MetaElementVO[] {
    if (!object || !Array.isArray(object)) {
      return new Array<MetaElementVO>();
    }
    const targetObjects: MetaElementVO[] = new Array<MetaElementVO>();
    for (const entry of object) {
      const property: MetaElementVO = ConverterHelper.convertToMetaElementVO(entry);
      if (property) {
        targetObjects.push(property);
      }
    }
    return targetObjects;
  }

  public static convertToMetaElementVO(object: any): MetaElementVO {
    if (!object || typeof object !== 'object') {
      return null;
    }
    const objectKeys: string[] = Object.keys(object);
    let targetKeys: string[] = Object.keys(new ConnectionAreaVO());
    if (ConverterHelper.hasObjectRequiredKeys(targetKeys, objectKeys)) {
      return JsonConvert.deserialize(object, 'ConnectionAreaVO');
    }
    targetKeys = Object.keys(new MontagePointAreaVO());
    if (ConverterHelper.hasObjectRequiredKeys(targetKeys, objectKeys)) {
      return JsonConvert.deserialize(object, 'MontagePointAreaVO');
    }
    targetKeys = Object.keys(new TextureAreaVO());
    if (ConverterHelper.hasObjectRequiredKeys(targetKeys, objectKeys)) {
      return JsonConvert.deserialize(object, 'TextureAreaVO');
    }
    return null;
  }

  public static convertToRepresentationDataVO(object: any): RepresentationDataVO {
    if (!object || typeof object !== 'object') {
      return null;
    }
    const type: string = object[this.TYPE_FIELD_NAME];
    if (type) {
      if (RepresentationDataVO.CLASS_NAME === type) {
        return JsonConvert.deserialize(object, 'RepresentationDataVO');
      }
      if (OBJRepresentationDataVO.CLASS_NAME === type) {
        return JsonConvert.deserialize(object, 'OBJRepresentationDataVO');
      }
    }
    return null;
  }

  public static convertToPropertyVOs(object: any): PropertyVO[] {
    if (!object || !Array.isArray(object)) {
      return new Array<PropertyVO>();
    }
    const targetObjects: PropertyVO[] = new Array<PropertyVO>();
    for (const entry of object) {
      const property: PropertyVO = ConverterHelper.convertToPropertyVO(entry);
      if (property) {
        targetObjects.push(property);
      }
    }
    return targetObjects;
  }

  public static convertToPropertyVO(object: any): PropertyVO {
    if (!object || typeof object !== 'object') {
      return null;
    }
    const objectKeys: string[] = Object.keys(object);
    let targetKeys: string[] = Object.keys(new ReportGenerationPropertyVO());
    if (ConverterHelper.hasObjectRequiredKeys(targetKeys, objectKeys)) {
      return JsonConvert.deserialize(object, 'ReportGenerationPropertyVO');
    }
    targetKeys = Object.keys(new PropertyVO());
    if (ConverterHelper.hasObjectRequiredKeys(targetKeys, objectKeys)) {
      const property: PropertyVO = JsonConvert.deserialize(object, 'PropertyVO');
      // 'json2typescript' library issue: converter type from decorator in 'values' is not included in mapping parameters
      if (!ArrayUtils.isEmpty(property.values)) {
        property.values = JsonConvert.deserialize(property.values, '[PropertyValueVO]');
      }
      return property;
    }
    return null;
  }

  public static convertToPropertyValueVOs(object: any): PropertyValueVO[] {
    if (!object || !Array.isArray(object)) {
      return new Array<PropertyValueVO>();
    }
    const targetObjects: PropertyValueVO[] = new Array<PropertyValueVO>();
    for (const entry of object) {
      const propertyValue: PropertyValueVO = ConverterHelper.convertToPropertyValueVO(entry);
      if (propertyValue) {
        targetObjects.push(propertyValue);
      }
    }
    return targetObjects;
  }

  public static convertToPropertyValueVO(object: any): PropertyValueVO {
    if (!object || typeof object !== 'object') {
      return null;
    }
    const objectKeys: string[] = Object.keys(object);
    let targetKeys: string[] = Object.keys(new LocalizedPropertyValueVO());
    if (ConverterHelper.hasObjectRequiredKeys(targetKeys, objectKeys)) {
      return JsonConvert.deserialize(object, 'LocalizedPropertyValueVO');
    }
    targetKeys = Object.keys(new PropertyValueVO());
    if (ConverterHelper.hasObjectRequiredKeys(targetKeys, objectKeys)) {
      return JsonConvert.deserialize(object, 'PropertyValueVO');
    }
    return null;
  }

  public static convertToStringArticleVODictionary(object: any): Dictionary<string, ArticleVO> {
    if (!object || typeof object !== 'object') {
      return null;
    }
    const stringArticleVODictionary: Dictionary<string, ArticleVO> = new Dictionary<string, ArticleVO>();
    const objectKeys: string[] = Object.keys(object);
    objectKeys.forEach((key: string) => {
      const articleVO: ArticleVO = ConverterHelper.convertToArticleVO(object[key]);
      stringArticleVODictionary.setValue(key, articleVO);
    });
    return stringArticleVODictionary;
  }

  public static convertToStringProjectVODictionary(object: any): Dictionary<string, ProjectVO> {
    if (!object || typeof object !== 'object') {
      return null;
    }
    const stringProjectVODictionary: Dictionary<string, ProjectVO> = new Dictionary<string, ProjectVO>();
    const objectKeys: string[] = Object.keys(object);
    objectKeys.forEach((key: string) => {
      const projectVO: ProjectVO = JsonConvert.deserialize(object[key], 'ProjectVO');
      stringProjectVODictionary.setValue(key, projectVO);
    });
    return stringProjectVODictionary;
  }


  public static convertToArticleTechnicalInfoMap(object: any): Map<number, ArticleTechnicalInfoVO> {
    if (!object || typeof object !== 'object') {
      return null;
    }
    const articleTechnicalInfoMap: Map<number, ArticleTechnicalInfoVO> = new Map<number, ArticleTechnicalInfoVO>();
    const objectKeys: string[] = Object.keys(object);
    objectKeys.forEach((key: string) => {
      const technicalInfo: ArticleTechnicalInfoVO = JsonConvert.deserialize(object[key], 'ArticleTechnicalInfoVO');
      articleTechnicalInfoMap.set(Number.parseInt(key, 10), technicalInfo);
    });
    return articleTechnicalInfoMap;
  }

  public static convertToLocalizedPropertyValueVOs(objects: any): LocalizedPropertyValueVO[] {
    if (!Array.isArray(objects)) {
      return null;
    }
    const values: LocalizedPropertyValueVO[] = [];
    for (const object of objects) {
      values.push(this.convertToLocalizedPropertyValueVO(object));
    }
    return values;
  }

  public static convertToLocalizedPropertyValueVO(object: any): LocalizedPropertyValueVO {
    if (!object || typeof object !== 'object') {
      return null;
    }
    if ('unit' in object) {
      return JsonConvert.deserialize(object, 'LocalizedPropertyValueWithUnitVO');
    } else {
      return JsonConvert.deserialize(object, 'LocalizedPropertyValueVO');
    }
  }


  public static convertToArticleVO(object: any): ArticleVO {
    if (!object || typeof object !== 'object') {
      return null;
    }
    const type: string = object['classType'];
    if (type) {
      if (ArticleVO.CLASS_NAME === type) {
        return JsonConvert.deserialize(object, 'ArticleVO');
      }
      if (CustomArticleVO.CLASS_NAME === type) {
        return JsonConvert.deserialize(object, 'CustomArticleVO');
      }
    }
    return null;
  }

  public static convertToArticleVOs(objects: any): ArticleVO[] {
    if (!Array.isArray(objects)) {
      return null;
    }
    const articles: ArticleVO[] = [];
    for (const object of objects) {
      const article: ArticleVO = ConverterHelper.convertToArticleVO(object);
      if (article) {
        articles.push(article);
      }
    }
    return articles;
  }

  public static convertToQuickFixActionVOs(object: any): QuickFixActionVO[] {
    if (!object || !Array.isArray(object)) {
      return new Array<QuickFixActionVO>();
    }
    const targetObjects: QuickFixActionVO[] = new Array<QuickFixActionVO>();
    for (const entry of object) {
      const quikFix: QuickFixActionVO = ConverterHelper.convertToQuickFixActionVO(entry);
      if (quikFix) {
        targetObjects.push(quikFix);
      }
    }
    return targetObjects;
  }

  public static convertToQuickFixActionVO(object: any): QuickFixActionVO {
    if (!object || typeof object !== 'object') {
      return null;
    }
    const type: string = object[this.TYPE_FIELD_NAME];
    if (type) {
      if (QuickFixActionVO.CLASS_NAME === type) {
        return JsonConvert.deserialize(object, 'QuickFixActionVO');
      }
      if (InsertArticleQuickFixActionVO.CLASS_NAME === type) {
        return JsonConvert.deserialize(object, 'InsertArticleQuickFixActionVO');
      }
      if (DeleteArticleQuickFixActionVO.CLASS_NAME === type) {
        return JsonConvert.deserialize(object, 'DeleteArticleQuickFixActionVO');
      }
      if (ApplyTextToTextureAreaUsageQuickFixActionVO.CLASS_NAME === type) {
        return JsonConvert.deserialize(object, 'ApplyTextToTextureAreaUsageQuickFixActionVO');
      }
    }
    return null;
  }

  public static convertToArticleDescriptorVO(object: any): ArticleDescriptorVO {
    if (!object || typeof object !== 'object') {
      return null;
    }
    const type: string = object[this.TYPE_FIELD_NAME];
    if (type) {
      if (ArticleDescriptorVO.CLASS_NAME === type) {
        return JsonConvert.deserialize(object, 'ArticleDescriptorVO');
      }
      if (ListArticleDescriptorVO.CLASS_NAME === type) {
        return JsonConvert.deserialize(object, 'ListArticleDescriptorVO');
      }
      if (SingleArticleDescriptorVO.CLASS_NAME === type) {
        return JsonConvert.deserialize(object, 'SingleArticleDescriptorVO');
      }
      if (UsageArticleDescriptorVO.CLASS_NAME === type) {
        return JsonConvert.deserialize(object, 'UsageArticleDescriptorVO');
      }
    }
    return null;
  }

  public static convertToElementReferenceVOs(object: any): ElementReferenceVO[] {
    if (!object || !Array.isArray(object)) {
      return new Array<ElementReferenceVO>();
    }
    const targetObjects: ElementReferenceVO[] = new Array<ElementReferenceVO>();
    for (const entry of object) {
      const elementReference: ElementReferenceVO = ConverterHelper.convertToElementReferenceVO(entry);
      if (elementReference) {
        targetObjects.push(elementReference);
      }
    }
    return targetObjects;
  }

  public static convertToElementReferenceVO(object: any): ElementReferenceVO {
    if (!object || typeof object !== 'object') {
      return null;
    }
    const type: string = object[this.TYPE_FIELD_NAME];
    if (type) {
      if (ElementReferenceVO.CLASS_NAME === type) {
        return JsonConvert.deserialize(object, 'ElementReferenceVO');
      }
      if (ArticleReferenceVO.CLASS_NAME === type) {
        return JsonConvert.deserialize(object, 'ArticleReferenceVO');
      }
      if (ArticleUsageInfoPropertyReferenceVO.CLASS_NAME === type) {
        return JsonConvert.deserialize(object, 'ArticleUsageInfoPropertyReferenceVO');
      }
      if (ArticleUsagePlausiPropertyReferenceVO.CLASS_NAME === type) {
        return JsonConvert.deserialize(object, 'ArticleUsagePlausiPropertyReferenceVO');
      }
      if (CalculatedArticlePropertyReferenceVO.CLASS_NAME === type) {
        return JsonConvert.deserialize(object, 'CalculatedArticlePropertyReferenceVO');
      }
      if (DefinedMetaElementPlausiPropertyReferenceVO.CLASS_NAME === type) {
        return JsonConvert.deserialize(object, 'DefinedMetaElementPlausiPropertyReferenceVO');
      }
    }
    return null;
  }
}
